import path from "node:path";
import * as p from "@clack/prompts";
import { AssetManager } from "@skaile/workspaces/asset-manager";
import {
  type DriverTarget,
  parseAssetRef,
  readLock,
  SUPPORTED_DRIVER_TARGETS,
} from "@skaile/workspaces/core";
import type { InstallManifest } from "@skaile/workspaces/types";
import { Command } from "commander";
import { colorRef, formatSoftDepHint, logErr, logInfo, logOk, logWarn, S } from "../helpers.ts";

/**
 * Canonical asset-ref shape accepted by `skaile install <ref>`:
 * `kind:@<publisher>/<name>#<version>` (the `#` version sigil). Validated via
 * `parseAssetRef` so the CLI shares the single canonical grammar; the legacy
 * trailing-`@version` shape is rejected.
 */
const REF_HINT = "kind:@<publisher>/<name>#<version> (e.g. skill:@skaile/use-exa#1.0.0)";

/** True when `ref` parses as a canonical asset ref. */
function isValidAssetRef(ref: string): boolean {
  try {
    parseAssetRef(ref);
    return true;
  } catch {
    return false;
  }
}

/**
 * Minimal catalog-source surface the pointer-only install path needs.
 * `RemoteCatalogSource` satisfies this — narrowing keeps {@link runPointerOnlyInstall}
 * testable with a hand-rolled stub.
 */
export interface InstallManifestSource {
  getInstallManifest(ref: string): Promise<InstallManifest>;
}

/**
 * Capability check: is this catalog source able to serve install manifests?
 *
 * `openCatalogSource` returns a `RemoteCatalogSource | RestCatalogSource`.
 * Only the tRPC-framed `RemoteCatalogSource` implements `getInstallManifest`;
 * the REST-framed `RestCatalogSource` (the default for any catalog URL other
 * than `https://skaile.store`) does not. Calling it unconditionally would be a
 * runtime `TypeError`, so the pointer-only install path must capability-detect
 * first and fail with a clear, actionable message.
 */
export function supportsInstallManifest(source: unknown): source is InstallManifestSource {
  return (
    typeof (source as { getInstallManifest?: unknown } | null | undefined)?.getInstallManifest ===
    "function"
  );
}

/**
 * Minimal library surface the pointer-only install path needs.
 * `LocalIndex` satisfies this.
 */
export interface InstallLibrary {
  install(manifest: InstallManifest): Promise<{ installPath: string }>;
}

/**
 * Result of a pointer-only catalog install.
 */
export interface PointerOnlyInstallResult {
  /** Absolute path the verified asset bytes were written to. */
  installPath: string;
  /** The pinned upstream commit SHA the asset was fetched at. */
  commitSha: string;
  /** Number of files in the install manifest. */
  fileCount: number;
}

/**
 * Pointer-only catalog install — the `skaile install <ref>` code path.
 *
 * 1. Resolves the pointer-only {@link InstallManifest} from the Catalog.
 * 2. Hands it to the library, which fetches each file's bytes directly from
 *    the upstream repo at the pinned commit, verifies every per-file SHA256
 *    plus the composite SHA256, and only then writes the verified files.
 *
 * Both dependencies are injected so the function is unit-testable without a
 * network round-trip. In production the CLI passes a `RemoteCatalogSource`
 * and a `LocalIndex`.
 *
 * @param ref - Asset ref (`<publisher>/<name>@<version>`, optional leading `@`).
 * @param deps - Injected catalog source + library.
 * @returns The install path, pinned commit SHA, and file count.
 * @throws When the ref is malformed, the manifest cannot be resolved, or any
 *   hash verification fails. The underlying error message is surfaced as-is.
 */
export async function runPointerOnlyInstall(
  ref: string,
  deps: { catalog: InstallManifestSource; library: InstallLibrary },
): Promise<PointerOnlyInstallResult> {
  let requested: ReturnType<typeof parseAssetRef>;
  try {
    requested = parseAssetRef(ref);
  } catch (e) {
    throw new Error(
      `malformed asset ref '${ref}' — expected ${REF_HINT}: ${e instanceof Error ? e.message : String(e)}`,
    );
  }
  const manifest = await deps.catalog.getInstallManifest(ref);
  // [M1] Identity check: the catalog must not install a different asset than
  // requested. Compare the PARSED refs (kind/publisher/name/pin) so equivalent
  // spellings (optional `kind:`, default `skill`) are treated as the same ref.
  const got = parseAssetRef(manifest.ref);
  if (
    got.kind !== requested.kind ||
    got.publisher !== requested.publisher ||
    got.name !== requested.name ||
    got.pin !== requested.pin
  ) {
    throw new Error(
      `catalog identity mismatch: requested '${ref}' but the catalog returned a manifest for '${manifest.ref}'`,
    );
  }
  const { installPath } = await deps.library.install(manifest);
  return {
    installPath,
    commitSha: manifest.source.commitSha,
    fileCount: manifest.files.length,
  };
}

/**
 * Creates the `skaile install` command.
 *
 * Two modes, selected by argument shape:
 *
 * - `skaile install` (no positional arg) — reads `skaile.yaml` and deploys all
 *   declared assets (and their transitive dependencies) to the configured
 *   driver target directory. Supports `--locked` for CI-mode install from a
 *   lock file.
 * - `skaile install <publisher>/<name>@<version>` — pointer-only catalog
 *   install: resolves the pointer-only install manifest from the configured
 *   Catalog, fetches each file's bytes directly from the upstream repo at the
 *   pinned commit, verifies every per-file + the composite SHA256, then writes
 *   the verified files into the local Library.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/project#make-install-command
 */
export function makeInstallCommand(): Command {
  return new Command("install")
    .description(`Install dependencies from skaile.yaml, or a single asset by ref (${REF_HINT})`)
    .argument("[ref]", `Asset ref to install from the Catalog (${REF_HINT})`)
    .option("--locked", "Install from lock file (CI mode — fails on drift)")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .option("--target <agent>", "Agent framework", "claude-code")
    .option(
      "--global",
      "Install the global SSOT (~/.skaile/skaile.yaml), fanning out to every global backend",
    )
    .option(
      "--backend <ids>",
      "Comma-separated global backends to install into (default: all resolved); implies --global",
    )
    .option(
      "--link",
      "symlink local-path assets instead of copying (dev loop; machine-local, not for committing)",
    )
    .action(runInstallCommand);
}

/** Options shape accepted by the `skaile install` command action. */
interface InstallCommandOptions {
  locked?: boolean;
  projectDir: string;
  target: string;
  global?: boolean;
  backend?: string;
  link?: boolean;
}

/**
 * Parse a `--backend a,b` value into validated {@link DriverTarget}s, exiting on
 * an unknown id. Returns `undefined` when the flag is absent (resolve the full
 * global set instead).
 */
function parseBackendFilter(raw: string | undefined): DriverTarget[] | undefined {
  if (!raw) return undefined;
  const ids = raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const out: DriverTarget[] = [];
  for (const id of ids) {
    if (!(SUPPORTED_DRIVER_TARGETS as string[]).includes(id)) {
      logErr(`unknown backend '${id}' — expected one of: ${SUPPORTED_DRIVER_TARGETS.join(", ")}`);
      process.exit(1);
    }
    out.push(id as DriverTarget);
  }
  return out.length > 0 ? out : undefined;
}

/**
 * `skaile install` action body. Dispatches by argument shape:
 * a positional `ref` selects pointer-only catalog install, otherwise the
 * legacy `skaile.yaml` install path runs.
 */
async function runInstallCommand(
  ref: string | undefined,
  opts: InstallCommandOptions,
): Promise<void> {
  // ── Pointer-only catalog install: `skaile install <ref>` ──────────────
  if (ref !== undefined) {
    await runRefInstall(ref, opts);
    return;
  }

  // ── Legacy install: `skaile install` reads skaile.yaml ────────────────
  await runYamlInstall(opts);
}

/** Pointer-only catalog install path for `skaile install <ref>`. */
async function runRefInstall(ref: string, opts: InstallCommandOptions): Promise<void> {
  if (!isValidAssetRef(ref)) {
    logErr(`malformed asset ref '${ref}' — expected ${REF_HINT}`);
    process.exit(1);
  }
  const spinner = p.spinner();
  spinner.start(`Installing ${ref}`);
  try {
    const { openCatalogSource, openLibrary } = await import("../open-library.ts");
    const catalog = await openCatalogSource({ projectDir: path.resolve(opts.projectDir) });
    // [H2] REST-framed catalogs (the default for any catalog URL other
    // than https://skaile.store) cannot serve install manifests. Detect
    // and fail clearly rather than throwing a runtime TypeError.
    if (!supportsInstallManifest(catalog)) {
      throw new Error(
        "pointer-only install (skaile install <ref>) requires a tRPC-framed Catalog. " +
          "The configured catalog uses REST framing, which does not serve install " +
          "manifests. Set catalog.url to https://skaile.store, or catalog.framing: trpc, " +
          "in ~/.skaile/config.yaml.",
      );
    }
    const library = await openLibrary();
    try {
      const result = await runPointerOnlyInstall(ref, { catalog, library });
      spinner.stop("Done");
      logOk(`Installed ${colorRef(ref)}`);
      logInfo(`Path: ${S.dim(result.installPath)}`);
      logInfo(`Commit: ${S.dim(result.commitSha)}`);
      logInfo(`Files: ${S.dim(String(result.fileCount))}`);
      logInfo(
        S.dim(`Installed globally (see Path above) — use \`skaile add ${ref}\` to deploy into this project.`),
      );
    } finally {
      library.close();
    }
  } catch (err) {
    spinner.stop("Failed");
    const msg = err instanceof Error ? err.message : String(err);
    if (/unreachable/i.test(msg)) {
      logErr(`asset uninstallable — source commit no longer available (${msg})`);
    } else {
      logErr(msg);
    }
    process.exit(1);
  }
}

/** Legacy install path for bare `skaile install` (reads skaile.yaml). */
async function runYamlInstall(opts: InstallCommandOptions): Promise<void> {
  const projectDir = path.resolve(opts.projectDir);

  const backends = parseBackendFilter(opts.backend);
  // `--backend` implies `--global`.
  if (opts.global || backends) {
    await runGlobalInstall(opts, backends);
    return;
  }

  // Hydrate sources declared in skaile.yaml before resolving assets, so
  // freshly checked-out projects pull their source clones automatically.
  try {
    const { ensureSourcesCloned } = await import("../ensure-sources.ts");
    const hydrate = ensureSourcesCloned(projectDir, { quiet: true });
    if (hydrate.cloned.length > 0) {
      logOk(`Cloned source(s): ${hydrate.cloned.join(", ")}`);
    }
    if (hydrate.failed.length > 0) {
      logErr(`Failed to clone source(s): ${hydrate.failed.join(", ")}`);
    }
  } catch (err) {
    logWarn(`source hydration skipped: ${err instanceof Error ? err.message : String(err)}`);
  }

  const am = new AssetManager({
    projectDir,
    driverTarget: opts.target as DriverTarget,
  });
  const spinner = p.spinner();
  spinner.start(opts.locked ? "Installing from lock file" : "Installing dependencies");
  try {
    const result = await am.install({ locked: opts.locked, link: opts.link });
    spinner.stop("Done");
    if (result.deployed.length > 0) {
      for (const d of result.deployed) logOk(d);
    }
    if (result.removed.length > 0) {
      for (const r of result.removed) logWarn(`Removed: ${r}`);
    }
    if (result.deployed.length === 0 && result.removed.length === 0) {
      logInfo("Nothing to install (already up to date).");
    }
    if (result.missing.length > 0) {
      for (const m of result.missing) logErr(`Missing: ${m}`);
      // Partial-miss must fail the process — CI can't otherwise distinguish a
      // clean install from "half the assets never resolved".
      process.exitCode = 1;
    }
    if (result.lockWritten) {
      logInfo(`Lock file written: ${S.dim("skaile.lock.yaml")}`);
    }
    // Report-only: surface soft deps the declared assets reference but aren't
    // present. Never a prompt — install is a batch/CI path. Skipped under
    // --locked: that mode is "restore exactly", so an `add` nudge would jar.
    if (!opts.locked) await reportInstalledSoftDeps(am);
  } catch (err) {
    spinner.stop("Failed");
    logErr(String(err));
    process.exit(1);
  }
}

/**
 * Report (never prompt) the body-link soft deps that the declared assets
 * reference but that aren't in the project — the `install` counterpart to the
 * `add`-time soft-dep surface. `install` is a batch/CI path (often non-TTY), so
 * it only ever prints a hint: pulling stays an explicit `skaile add`. Best-effort
 * — `reportSoftDeps` swallows any index failure and returns `[]`.
 */
export async function reportInstalledSoftDeps(
  am: Pick<AssetManager, "listDeclared" | "reportSoftDeps">,
  onHint: (line: string) => void = (l) => logInfo(S.dim(l)),
): Promise<void> {
  try {
    const declared = (await am.listDeclared()).map((d) => d.ref);
    if (declared.length === 0) return;
    const edges = await am.reportSoftDeps(declared);
    for (const edge of edges) onHint(formatSoftDepHint(edge));
  } catch {
    // Best-effort: a soft-dep report must never fail an install that succeeded.
  }
}

/**
 * Global cross-backend install: deploy every dep declared in the global SSOT
 * (`~/.skaile/skaile.yaml`) into each resolved global backend's dir under `~`,
 * write the global lock (`~/.skaile/skaile.lock.yaml`), then print per-backend
 * state. `backends` is the `--backend` filter (omit for the full resolved set).
 */
async function runGlobalInstall(
  opts: InstallCommandOptions,
  backends: DriverTarget[] | undefined,
): Promise<void> {
  const am = new AssetManager({
    projectDir: path.resolve(opts.projectDir),
    global: true,
    backends,
  });
  const spinner = p.spinner();
  spinner.start("Installing global dependencies");
  try {
    const result = await am.install({ locked: opts.locked, link: opts.link });
    spinner.stop("Done");
    if (result.deployed.length > 0) for (const d of result.deployed) logOk(d);
    if (result.removed.length > 0) for (const r of result.removed) logWarn(`Removed: ${r}`);
    if (result.deployed.length === 0 && result.removed.length === 0)
      logInfo("Nothing to install (already up to date).");
    if (result.missing.length > 0) {
      for (const m of result.missing) logErr(`Missing: ${m}`);
      process.exitCode = 1;
    }
    if (result.lockWritten) logInfo(`Global lock written: ${S.dim("~/.skaile/skaile.lock.yaml")}`);

    // Per-backend reconciliation summary so a partial fan-out is visible.
    const state = am.globalState({});
    const byBackend = new Map<string, number>();
    for (const s of state)
      if (s.state === "installed") byBackend.set(s.backend, (byBackend.get(s.backend) ?? 0) + 1);
    for (const [backend, n] of byBackend) logInfo(`${backend}: ${n} installed`);
  } catch (err) {
    spinner.stop("Failed");
    logErr(String(err));
    process.exit(1);
  }
}

/**
 * Creates the `skaile check` command.
 *
 * Scans all deployed assets for unmet dependency requirements **and** the
 * declared set for deps that resolved into the lock but never materialized, and
 * exits with code 1 if either is found. `doctor()` alone only walks *deployed*
 * assets' `requires`, so a declared dep whose bytes never landed (e.g. a
 * store-sourced `mcp-server`) used to report "satisfied" (#462).
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/project#make-check-command
 */
export function makeCheckCommand(): Command {
  return new Command("check")
    .description("Check for unmet requirements")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(async (opts) => {
      const am = new AssetManager({ projectDir: path.resolve(opts.projectDir) });
      const issues = await am.doctor();
      // A project with no (or an unreadable) skaile.yaml has no declared set to
      // reconcile — treat it as drift-free so `check` behaves as it always has.
      let drifted: Awaited<ReturnType<typeof am.listDeclared>> = [];
      try {
        drifted = (await am.listDeclared()).filter((d) => d.state === "drift");
      } catch {
        // ignored — doctor() below is still authoritative for deployed assets.
      }
      if (issues.length === 0 && drifted.length === 0) {
        logOk("All dependencies satisfied.");
      } else {
        for (const i of issues) {
          logErr(
            `${i.assetKind}:${i.assetName} requires ${i.depKind}:${i.depName} (${i.inRepos ? "fixable" : "not found"})`,
          );
        }
        for (const d of drifted) {
          logErr(`${d.ref} is declared and locked but not materialized — run \`skaile install\``);
        }
        process.exit(1);
      }
    });
}

/**
 * Creates the `skaile clean` command.
 *
 * Removes all skaile-managed assets tracked in `skaile.lock.yaml` from the
 * workspace. With `--all` also removes history, patches, repos, and the lock
 * file itself. Preserves unmanaged (manually placed) assets.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/project#make-clean-command
 */
export function makeCleanCommand(): Command {
  return new Command("clean")
    .description("Remove all skaile-managed assets from the workspace")
    .option("--all", "Full reset: also remove history, patches, repos, and lock file")
    .option("--dry-run", "Show what would be removed without doing it")
    .option("-y, --yes", "Skip confirmation prompt")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .option("--target <agent>", "Agent framework", "claude-code")
    .action(async (opts) => {
      const am = new AssetManager({
        projectDir: path.resolve(opts.projectDir),
        driverTarget: opts.target as DriverTarget,
      });

      // Preview: load lock file to show what will be cleaned
      const lock = readLock(path.resolve(opts.projectDir, "skaile.lock.yaml"));

      if (!lock || Object.keys(lock.assets).length === 0) {
        logInfo("Nothing to clean (no skaile-managed assets found).");
        return;
      }

      const assetRefs = Object.keys(lock.assets);
      console.log();
      console.log(`  ${S.heading("Assets to remove:")} ${S.dim(`(${assetRefs.length})`)}`);
      for (const ref of assetRefs) {
        console.log(`    ${colorRef(ref)}`);
      }

      if (opts.all) {
        console.log();
        console.log(`  ${S.heading("Also removing:")}`);
        console.log(`    ${S.dim(".skaile/history.yaml")}`);
        console.log(`    ${S.dim(".skaile/patches/")}`);
        console.log(`    ${S.dim("skaile.lock.yaml")}`);
      }

      if (opts.dryRun) {
        console.log();
        logInfo("Dry run — no changes made.");
        return;
      }

      if (!opts.yes) {
        const confirm = await p.confirm({ message: "Proceed?" });
        if (p.isCancel(confirm) || !confirm) {
          logInfo("Cancelled.");
          return;
        }
      }

      const result = am.clean({ all: opts.all });

      console.log();
      for (const r of result.removed) {
        logOk(`Removed ${r}`);
      }
      for (const s of result.skipped) {
        logWarn(`Skipped ${s.ref}: ${s.reason}`);
      }
      if (result.unmanaged.length > 0) {
        console.log();
        logInfo(`Kept ${result.unmanaged.length} unmanaged asset(s):`);
        for (const u of result.unmanaged) {
          console.log(`    ${S.dim(u)}`);
        }
      }
      if (opts.all) {
        console.log();
        logOk("Full reset complete.");
      }
    });
}
